import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import {
    Box, Button, Typography, Container, Tabs, Tab,
    ToggleButtonGroup, ToggleButton, useTheme,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    ThumbUp as ThumbUpIcon,
    Comment as CommentIcon,
    TrendingUp as TrendingUpIcon,
    VideoLibrary as VideoLibraryIcon,
    LocalFireDepartment as FireIcon,
    BarChart as BarChartIcon,
    PeopleAlt as PeopleAltIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';

import { platformColor } from './constants';
import { DashboardLoading, DashboardEmpty } from './DashboardStates';
import PageAlert from '../PageAlert';
import StatCard from './StatCard';
import PlatformTable from './PlatformTable';
import PostsOverTimeChart from './PostsOverTimeChart';
import ContentTypeChart from './ContentTypeChart';
import TopPostsList from './TopPostsList';
import ConnectPlatformsCTA from '../ConnectPlatformsCTA';
import PostStatsDrawer from '../PostStatsDrawer';
import FollowerStatsSection from './FollowerStatsSection';
import { importedPostsApi, analyticsApi } from '../../services/api';

const PLATFORM_META = {
    youtube:   { label: 'YouTube',   Icon: YouTubeIcon,   color: '#FF0000' },
    instagram: { label: 'Instagram', Icon: InstagramIcon, color: '#E1306C' },
    facebook:  { label: 'Facebook',  Icon: FacebookIcon,  color: '#1877F2' },
};

const DATE_RANGES = [
    { id: '7D',  label: '7D',  days: 7 },
    { id: '30D', label: '30D', days: 30 },
    { id: '3M',  label: '3M',  days: 90 },
    { id: '1Y',  label: '1Y',  days: 365 },
    { id: 'ALL', label: 'All',  days: null },
];

function sinceFromRange(rangeId) {
    const r = DATE_RANGES.find(d => d.id === rangeId);
    if (!r || !r.days) return null;
    const d = new Date();
    d.setDate(d.getDate() - r.days);
    return d.toISOString();
}

// ── Overview sub-component ────────────────────────────────────────────────────
const OverviewTab = ({ analytics, onRefresh }) => {
    const chartData = useMemo(() =>
        (analytics || [])
            .filter(item => item?.platform)
            .map((item, index) => ({
                platform: item.platform.charAt(0).toUpperCase() + item.platform.slice(1),
                rawPlatform: item.platform,
                views:    Math.max(0, item.views    || 0),
                likes:    Math.max(0, item.likes    || 0),
                comments: Math.max(0, item.comments || 0),
                shares:   Math.max(0, item.shares   || 0),
                posts:    Math.max(0, item.post_count || 0),
                color: platformColor(item.platform, index),
                engagement:
                    (item.views || 0) > 0
                        ? Math.round(((item.likes || 0) + (item.comments || 0)) / item.views * 10000) / 100
                        : 0,
            })),
        [analytics]
    );

    const totals = useMemo(() => ({
        views:    chartData.reduce((s, d) => s + d.views, 0),
        likes:    chartData.reduce((s, d) => s + d.likes, 0),
        comments: chartData.reduce((s, d) => s + d.comments, 0),
        posts:    chartData.reduce((s, d) => s + d.posts, 0),
    }), [chartData]);

    const avgEngagement = useMemo(() => {
        if (!chartData.length) return 0;
        return Math.round(chartData.reduce((s, d) => s + d.engagement, 0) / chartData.length * 100) / 100;
    }, [chartData]);

    return (
        <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2.5, mb: 3 }}>
                <StatCard title="Total Views"    value={totals.views}         icon={<VisibilityIcon sx={{ fontSize: 24 }} />} color="#6366f1" subtitle="Across all platforms" />
                <StatCard title="Total Likes"    value={totals.likes}         icon={<ThumbUpIcon sx={{ fontSize: 24 }} />}    color="#0284c7" subtitle="Engagement received" />
                <StatCard title="Total Comments" value={totals.comments}      icon={<CommentIcon sx={{ fontSize: 24 }} />}    color="#0d9488" subtitle="Community interaction" />
                <StatCard title="Avg Engagement" value={`${avgEngagement}%`}  icon={<TrendingUpIcon sx={{ fontSize: 24 }} />} color="#059669" subtitle="(Likes + Comments) / Views" />
            </Box>
            <FollowerStatsSection />
            <Box sx={{ mt: 3 }}>
                <PlatformTable chartData={chartData} />
            </Box>
        </>
    );
};

// ── Per-platform sub-component ────────────────────────────────────────────────
const PlatformTab = ({ platform, onPostClick }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const meta = PLATFORM_META[platform] || { color: '#6366f1', label: platform };
    const [dateRange, setDateRange] = useState('ALL');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [followerData, setFollowerData] = useState(null);

    const fetchData = useCallback(async (range) => {
        setLoading(true);
        setError('');
        try {
            const since = sinceFromRange(range);
            const result = await importedPostsApi.getDashboard({ platform, since });
            setData(result);
        } catch (err) {
            setError(err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [platform]);

    // Fetch follower counts once per platform mount
    useEffect(() => {
        analyticsApi.getFollowers(30)
            .then(res => setFollowerData(res?.current?.[platform] ?? null))
            .catch(() => setFollowerData(null));
    }, [platform]);

    useEffect(() => { fetchData(dateRange); }, [dateRange, fetchData]);

    const totals = data?.totals || {};
    const hasData = totals.post_count > 0;

    // Follower label differs by platform
    const followerLabel = platform === 'youtube' ? 'Subscribers' : 'Followers';
    const followerCount = followerData?.count ?? null;
    const delta1d = followerData?.delta_1d ?? null;
    const delta7d = followerData?.delta_7d ?? null;

    function DeltaPill({ value, label }) {
        if (value == null) return null;
        const pos = value > 0;
        const neu = value === 0;
        const Icon = neu ? RemoveIcon : pos ? ArrowUpwardIcon : ArrowDownwardIcon;
        const colour = neu ? '#64748b' : pos ? '#10b981' : '#ef4444';
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.4,
                    px: 1, py: 0.3, borderRadius: 99,
                    bgcolor: `${colour}18`, border: `1px solid ${colour}30`,
                }}>
                    <Icon sx={{ fontSize: 11, color: colour }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colour }}>
                        {pos ? '+' : ''}{value}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 600 }}>{label}</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Date range selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mr: 1 }}>Range:</Typography>
                <ToggleButtonGroup
                    value={dateRange}
                    exclusive
                    onChange={(_, v) => v && setDateRange(v)}
                    size="small"
                    sx={{ bgcolor: 'background.paper' }}
                >
                    {DATE_RANGES.map(r => (
                        <ToggleButton key={r.id} value={r.id} sx={{ px: 2, fontWeight: 700, fontSize: '0.8rem' }}>
                            {r.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchData(dateRange)}
                    sx={{ ml: 'auto', borderRadius: 2 }}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Box sx={{ mb: 2 }}>
                    <PageAlert error={error} />
                </Box>
            )}

            {loading ? (
                <DashboardLoading />
            ) : !hasData ? (
                <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed', borderColor: 'divider', borderRadius: 4 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>No synced posts yet</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Go to Past Posts and click "Sync {meta.label}" to import your content.
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* ── Follower / Subscriber hero card ── */}
                    <Box sx={{
                        position: 'relative', overflow: 'hidden',
                        borderRadius: 4, mb: 3,
                        border: `1.5px solid ${theme.palette.mode === 'dark' ? `${meta.color}30` : `${meta.color}20`}`,
                        background: isDark
                            ? `linear-gradient(135deg, ${meta.color}12 0%, #0f172a 100%)`
                            : `linear-gradient(135deg, ${meta.color}08 0%, #fff 100%)`,
                        p: { xs: 2.5, sm: 3.5 },
                        display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
                    }}>
                        {/* Ambient glow */}
                        <Box sx={{
                            position: 'absolute', top: -60, right: -60,
                            width: 200, height: 200, borderRadius: '50%', pointerEvents: 'none',
                            background: `radial-gradient(circle, ${meta.color}25 0%, transparent 70%)`,
                        }} />

                        {/* Icon */}
                        <Box sx={{
                            width: 56, height: 56, borderRadius: 3, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: `${meta.color}18`, border: `1.5px solid ${meta.color}35`,
                        }}>
                            <PeopleAltIcon sx={{ fontSize: 28, color: meta.color }} />
                        </Box>

                        {/* Count block */}
                        <Box sx={{ flex: 1, minWidth: 120 }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.25 }}>
                                {meta.label} {followerLabel}
                            </Typography>
                            {followerCount != null ? (
                                <>
                                    <Typography sx={{ fontSize: { xs: '2rem', sm: '2.6rem' }, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', color: 'text.primary' }}>
                                        {followerCount >= 1_000_000
                                            ? `${(followerCount / 1_000_000).toFixed(2)}M`
                                            : followerCount >= 1_000
                                                ? `${(followerCount / 1_000).toFixed(1)}K`
                                                : followerCount.toLocaleString()}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                                        <DeltaPill value={delta1d} label="today" />
                                        <DeltaPill value={delta7d} label="7 days" />
                                    </Box>
                                </>
                            ) : (
                                <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}>
                                    Syncing… check back after the next daily snapshot.
                                </Typography>
                            )}
                        </Box>

                        {/* Right label */}
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600, alignSelf: 'flex-end' }}>
                            Live count · updated daily
                        </Typography>
                    </Box>

                    {/* KPI row — 5 columns using CSS grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2.5, mb: 3 }}>
                        <StatCard title="Posts"      value={totals.post_count}           icon={<VideoLibraryIcon sx={{ fontSize: 22 }} />} color={meta.color} subtitle="Synced posts" />
                        <StatCard title="Views"      value={totals.views}                icon={<VisibilityIcon sx={{ fontSize: 22 }} />}  color="#6366f1"   subtitle="Total impressions" />
                        <StatCard title="Likes"      value={totals.likes}                icon={<ThumbUpIcon sx={{ fontSize: 22 }} />}     color="#0284c7"   subtitle="Total likes" />
                        <StatCard title="Comments"   value={totals.comments}             icon={<CommentIcon sx={{ fontSize: 22 }} />}     color="#0d9488"   subtitle="Total comments" />
                        <StatCard title="Engagement" value={`${totals.engagement_rate}%`} icon={<TrendingUpIcon sx={{ fontSize: 22 }} />} color="#059669"   subtitle="(Likes+Comments)/Views" />
                    </Box>

                    {/* Charts row */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2.5, mb: 3 }}>
                        <PostsOverTimeChart data={data.posts_over_time} color={meta.color} height={240} />
                        <ContentTypeChart data={data.content_breakdown} height={240} />
                    </Box>

                    {/* Top posts + viral */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: data.viral_posts?.length > 0 ? '1fr 1fr' : '1fr' }, gap: 2.5 }}>
                        <TopPostsList posts={data.top_posts} title="Top Performing Posts" icon={<BarChartIcon />} onPostClick={onPostClick} />
                        {data.viral_posts?.length > 0 && (
                            <TopPostsList posts={data.viral_posts} viral title="🔥 Viral Posts" icon={<FireIcon />} onPostClick={onPostClick} />
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = ({ analytics, loading, onRefresh, error }) => {
    const hasAutoFetched = useRef(false);
    const [tab, setTab] = useState(0);
    const [selectedPostId, setSelectedPostId] = useState(null);

    const errorMsg = typeof error === 'string' ? error : (error?.message || '');
    const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

    useEffect(() => {
        if ((!analytics || analytics.length === 0) && !loading && !errorMsg && !hasAutoFetched.current && onRefresh) {
            hasAutoFetched.current = true;
            onRefresh();
        }
    }, [onRefresh, analytics, loading, errorMsg]);

    // Derive available platforms from analytics data
    const availablePlatforms = useMemo(() =>
        (analytics || []).map(a => a.platform).filter(p => PLATFORM_META[p]),
        [analytics]
    );

    const tabs = ['Overview', ...availablePlatforms.map(p => PLATFORM_META[p]?.label || p)];

    if (loading) return <Box><PageAlert error={error} /><DashboardLoading /></Box>;
    if (isNoAccountsError) return (
        <Box sx={{ py: 6 }}>
            <ConnectPlatformsCTA
                title="Dashboard Locked"
                description="Connect your social media accounts to see live analytics, engagement trends, and growth metrics."
            />
        </Box>
    );
    if (!analytics || analytics.length === 0) return <Box><PageAlert error={error} /><DashboardEmpty onRefresh={onRefresh} /></Box>;

    return (
        <>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
            <PageAlert error={error} />

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 900,
                            letterSpacing: '-0.03em',
                            mb: 0.5,
                            fontSize: { xs: '1.75rem', md: '2.25rem' },
                            background: 'linear-gradient(135deg, #1e293b, #3b82f6)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Analytics Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {availablePlatforms.length} platform{availablePlatforms.length !== 1 ? 's' : ''} connected
                        {analytics[0]?.post_count ? ` · ${analytics.reduce((s, a) => s + (a.post_count || 0), 0)} total posts` : ''}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    sx={{
                        borderRadius: 3, px: 3,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 6px 16px -4px rgba(99,102,241,0.4)',
                        '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                    }}
                >
                    Update Analytics
                </Button>
            </Box>

            {/* Platform tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
                variant="scrollable"
                scrollButtons="auto"
            >
                {tabs.map((label, i) => {
                    const platform = availablePlatforms[i - 1];
                    const meta = platform ? PLATFORM_META[platform] : null;
                    const Icon = meta?.Icon;
                    return (
                        <Tab
                            key={label}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {Icon && <Icon sx={{ fontSize: 16, color: tab === i ? meta.color : 'inherit' }} />}
                                    {label}
                                </Box>
                            }
                            sx={{ fontWeight: 700, textTransform: 'none', minHeight: 48 }}
                        />
                    );
                })}
            </Tabs>

            {/* Tab content */}
            {tab === 0 ? (
                <OverviewTab analytics={analytics} onRefresh={onRefresh} />
            ) : (
                <PlatformTab platform={availablePlatforms[tab - 1]} onPostClick={setSelectedPostId} />
            )}
        </Container>

        {/* Per-post detail drawer */}
        <PostStatsDrawer
            postId={selectedPostId}
            open={!!selectedPostId}
            onClose={() => setSelectedPostId(null)}
        />
        </>
    );
};

export default Dashboard;
